import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw } from "lucide-react";
import ActualSekAmountField from "@/components/shared/ActualSekAmountField";
import { parseActualSekAmount } from "@/components/shared/actualSekAmount";
import { format } from "date-fns";
import { formatAmount } from "@financialharmony/ui";
import { SalesReport, TransactionSuggestion } from "@/types/salesReport";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useMarkAsReceivedFailure } from "@/i18n/useMatching";
import { settledRefusalMessage, SUGGESTION_ENQUEUE_FAILED } from "@/utils/errorUtils";
import { fetchWithPrecomputedFallback } from "@/utils/matchSuggestionFallback";

interface MarkAsReceivedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesReport: SalesReport;
  hasBankAccounts: boolean;
  onComplete: () => void;
}

/**
 * Issue #457: try the precomputed auto_match_decisions suggestion first (already computed by the
 * async match_jobs pipeline); fall back to the live JIT scoring query only when that's empty.
 * Sales reports aren't yet part of that pipeline (see
 * MatchingService.getPrecomputedTransactionSuggestionsForSalesReport's docstring), so the
 * precomputed call always resolves to [] today and this silently falls through to the unchanged
 * JIT path — kept for symmetry with the other three entry points and so this starts surfacing
 * precomputed suggestions automatically the day sales reports join the pipeline. `forceRefresh`
 * (the explicit "Search again" action) always calls the JIT path directly.
 */
function fetchSuggestions(
  salesReportId: string,
  forceRefresh: boolean,
): Promise<TransactionSuggestion[]> {
  const service = ServiceRegistry.getSalesReportService();
  return fetchWithPrecomputedFallback(
    () => service.getPrecomputedTransactionSuggestions(salesReportId),
    () => service.getTransactionSuggestions(salesReportId),
    forceRefresh,
  );
}

const MarkAsReceivedModal: React.FC<MarkAsReceivedModalProps> = ({
  open,
  onOpenChange,
  salesReport,
  hasBankAccounts,
  onComplete,
}) => {
  const t = useTranslations("consultantMatching.salesReport");
  const tRefusals = useTranslations("consultantMatching.refusals");
  const markAsReceivedFailure = useMarkAsReceivedFailure();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [suggestions, setSuggestions] = useState<TransactionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [actualSekAmountInput, setActualSekAmountInput] = useState("");

  useEffect(() => {
    if (open && hasBankAccounts) {
      setLoading(true);
      setSelectedTransactionId(null);
      fetchSuggestions(salesReport.id, false)
        .then(setSuggestions)
        .catch((err) => {
          console.error("Failed to fetch transaction suggestions:", err);
          setSuggestions([]);
        })
        .finally(() => setLoading(false));
    }
    if (open) {
      // Prefill with the sales report's current SEK estimate — confirming the default
      // is a single click; the user only edits it when the real amount differed.
      setActualSekAmountInput(salesReport.sekAmount != null ? String(salesReport.sekAmount) : "");
    }
    if (!open) {
      setSuggestions([]);
      setSelectedTransactionId(null);
      setActualSekAmountInput("");
    }
  }, [open, salesReport.id, salesReport.sekAmount, hasBankAccounts]);

  const handleSearchAgain = () => {
    setLoading(true);
    setSelectedTransactionId(null);
    fetchSuggestions(salesReport.id, true)
      .then(setSuggestions)
      .catch((err) => {
        console.error("Failed to fetch transaction suggestions:", err);
        setSuggestions([]);
      })
      .finally(() => setLoading(false));
  };

  const isForeignCurrency = salesReport.currency !== "SEK";
  const { amount: parsedActualSekAmount } = parseActualSekAmount(actualSekAmountInput);
  const isActualSekAmountValid = parsedActualSekAmount !== null;

  const handleSelectSuggestion = (suggestion: TransactionSuggestion) => {
    setSelectedTransactionId(suggestion.transactionId);
    if (isForeignCurrency) {
      // Lock the field to the real transaction amount — there's no separate
      // real-match backend path for sales reports, so this is the value that
      // will actually be submitted via the same external-match call.
      setActualSekAmountInput(String(suggestion.amount));
    }
  };

  const handleMarkAsReceived = async () => {
    if (isForeignCurrency && !isActualSekAmountValid) {
      return;
    }
    setSubmitting(true);
    try {
      const service = ServiceRegistry.getSalesReportService();
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      // selectedTransactionId is a suggestion's effective id (getEffectiveTransactionId), not
      // necessarily bank_transactions.id — the backend needs the suggestion's own accountId to
      // resolve it to the real internal row (issue #834's same root-cause class).
      const selectedSuggestion = suggestions.find((s) => s.transactionId === selectedTransactionId);
      await service.markAsReceivedExternally(
        salesReport.id,
        formattedDate,
        isForeignCurrency ? parsedActualSekAmount : undefined,
        selectedTransactionId ?? undefined,
        selectedSuggestion?.accountId,
      );
      toast.success(t("toasts.successTitle"), { description: t("toasts.successBody") });
      onComplete();
    } catch (error) {
      console.error("Failed to mark as received:", error);
      const named = markAsReceivedFailure(error);
      if (named) {
        toast.error(named.title, { description: named.description });
        // The report itself was marked received; only its bookkeeping is missing.
        if (named.code === SUGGESTION_ENQUEUE_FAILED) onComplete();
        return;
      }
      const settled = settledRefusalMessage(error);
      toast.error(settled ? tRefusals("transactionAlreadySettled") : t("toasts.errorTitle"), {
        description: settled ?? t("toasts.failedBody"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Card amount for display
  const cardAmount = salesReport.paymentMethods?.find(pm => pm.method === 'card')?.amount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
        </DialogHeader>

        {hasBankAccounts ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {cardAmount
                ? t("hintWithAmount", { amount: formatAmount(cardAmount), currency: salesReport.currency })
                : t("hint")}
            </p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : suggestions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("columns.description")}</TableHead>
                    <TableHead>{t("columns.amount")}</TableHead>
                    <TableHead>{t("columns.date")}</TableHead>
                    <TableHead>{t("columns.confidence")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.map((s) => (
                    <TableRow
                      key={s.transactionId}
                      className={`cursor-pointer ${selectedTransactionId === s.transactionId ? 'bg-accent' : ''}`}
                      onClick={() => handleSelectSuggestion(s)}
                    >
                      <TableCell>{s.description}</TableCell>
                      <TableCell>{formatAmount(s.amount)} {s.currency}</TableCell>
                      <TableCell>{s.date}</TableCell>
                      <TableCell>
                        <Badge variant={s.confidence >= 70 ? "default" : "secondary"}>
                          {s.confidence}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>{t("noneFound")}</p>
                <p className="text-xs mt-1">{t("orDate")}</p>
              </div>
            )}

            {!loading && (
              <div className="text-center">
                <Button variant="link" size="sm" onClick={handleSearchAgain}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  {t("searchAgain")}
                </Button>
              </div>
            )}

            {(suggestions.length === 0 || !selectedTransactionId) && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">{t("orSelectDate")}</p>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("noAccounts")}
            </p>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border"
            />
          </div>
        )}

        {isForeignCurrency && (
          <ActualSekAmountField
            id="sales-report-actual-sek-amount"
            value={actualSekAmountInput}
            onChange={setActualSekAmountInput}
            readOnly={!!selectedTransactionId}
            className="mt-4"
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleMarkAsReceived}
            disabled={submitting || (isForeignCurrency && !isActualSekAmountValid)}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {selectedTransactionId ? t("matchTransaction") : t("markReceived")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkAsReceivedModal;
